import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';

type Breadcrumb = {
  label: string;
  href?: string;
  /** When set to 'workflow', renders a WorkflowSwitcher instead of static text */
  type?: 'workflow';
  /** Extra context for the breadcrumb (e.g. app IDs for workflow switcher) */
  meta?: { project?: string | undefined;} | undefined;
};

type LayoutContextValue = {
  breadcrumbs: Breadcrumb[];
  setBreadcrumbs: (breadcrumbs: Breadcrumb[]) => void;
};

const LayoutContext = createContext<LayoutContextValue | undefined>(undefined);

type LayoutProviderProps = {
  children: ReactNode;
};

export const LayoutProvider = ({ children }: LayoutProviderProps) => {
  const [breadcrumbs, setBreadcrumbsState] = useState<Breadcrumb[]>([]);

  const setBreadcrumbs = useCallback((newBreadcrumbs: Breadcrumb[]) => {
    setBreadcrumbsState(newBreadcrumbs);
  }, []);

  const value = useMemo(
    () => ({ breadcrumbs, setBreadcrumbs }),
    [breadcrumbs, setBreadcrumbs]
  );

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = (): LayoutContextValue => {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};
